import { useState, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { LogOut, ChevronDown, ChevronRight, ChevronsLeft, ChevronsRight, MapPin, Package, Timer, Users, Download, type AppIcon } from './components/ui/icons';
import {
  PiGameControllerDuotone, PiGraduationCapDuotone, PiGearSixDuotone, PiSparkleDuotone, PiSquaresFourDuotone, PiBellDuotone,
  PiBroadcastDuotone, PiHeartbeatDuotone, PiWrenchDuotone, PiCloudCheckDuotone, PiChartPieSliceDuotone, PiWaveformDuotone,
  PiReceiptDuotone, PiClockCounterClockwiseDuotone, PiGaugeDuotone, PiEnvelopeDuotone,
} from 'react-icons/pi';
import HomestreamLogo from './components/ui/HomestreamLogo';
import Card from './components/ui/Card';
import LiveUrlGameIndexingPanel from './components/LiveUrlGameIndexing/LiveUrlGameIndexingPanel';
import GamesPanel from './components/Games/GamesPanel';
import SeasonsPanel from './components/Seasons/SeasonsPanel';
import TeamsPanel from './components/Teams/TeamsPanel';
import VenuesPanel from './components/Venues/VenuesPanel';
import ProductsPanel from './components/Products/ProductsPanel';
import PreviewUrlTimerSettings from './components/Products/PreviewUrlTimerSettings';
import InternalAccountsSettings from './components/Products/InternalAccountsSettings';
import UserTeamExportSettings from './components/Products/UserTeamExportSettings';
import AnalyticsPanel from './components/Analytics/AnalyticsPanel';
import MockGeniusPanel from './components/MockGenius/MockGeniusPanel';
import ManualOperationsPanel from './components/ManualOperations/ManualOperationsPanel';
import SyncDetectionPanel from './components/ManualOperations/SyncDetection/SyncDetectionPanel';
import ServiceHealthPanel from './components/ManualOperations/ServiceHealth/ServiceHealthPanel';
import InfrastructurePanel from './components/ManualOperations/Infrastructure/InfrastructurePanel';
import ApiHealthPanel from './components/ManualOperations/ApiHealth/ApiHealthPanel';
import ManualOperationPanel from './components/ManualOperations/ManualOperation/ManualOperationPanel';
import PurchasersPanel from './components/Analytics/Purchasers/PurchasersPanel';
import PurchaseTimelinePanel from './components/Analytics/PurchaseTimeline/PurchaseTimelinePanel';
import PaywallCounterPanel from './components/Analytics/PaywallCounter/PaywallCounterPanel';
import ContactCounterPanel from './components/Analytics/ContactCounter/ContactCounterPanel';
import Login from './components/Auth/Login';
import { authApi, THEME_MODE } from './services/api';
import Colors, { toRgbChannels, withAlpha } from './constants/Colors';
import './styles/index.css';
import ExecutionsPanel from 'components/Executions/ExecutionsPanel';

Object.entries(Colors).forEach(([key, value]) => {
  const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
  document.documentElement.style.setProperty(`--color-${kebabKey}`, value);
  document.documentElement.style.setProperty(`--color-${kebabKey}-rgb`, toRgbChannels(value));
});

type TabType = 'analytics' | 'mock-genius' | 'games' | 'teams' | 'venues' | 'live-indexing' | 'image-matching' | 'scheduler' | 'seasons' | 'executions'
  | 'iap-notification' | 'sync-detection' | 'service-health' | 'infrastructure' | 'api-health' | 'manual-operation' | 'purchasers' | 'purchase-timeline' | 'paywall-counter' | 'contact-counter';

const MANUAL_OPERATION_TABS: TabType[] = ['iap-notification', 'sync-detection', 'service-health', 'infrastructure', 'manual-operation', 'api-health'];
const ANALYTICS_TABS: TabType[] = ['analytics', 'purchasers', 'purchase-timeline', 'paywall-counter', 'contact-counter'];
type SettingsSection = 'venue-bot-mapping' | 'affiliate-offer' | 'preview-url-timer' | 'internal-accounts' | 'user-team-export';

const SETTINGS_SECTIONS: { key: SettingsSection; label: string; icon: AppIcon }[] = [
  { key: 'venue-bot-mapping', label: 'Venue Bot Mapping', icon: MapPin },
  { key: 'affiliate-offer', label: 'Affiliate Offer', icon: Package },
  { key: 'preview-url-timer', label: 'Preview Timer', icon: Timer },
  { key: 'internal-accounts', label: 'Internal Accounts', icon: Users },
  { key: 'user-team-export', label: 'User Team Export', icon: Download },
];

const AUTH_STORAGE_KEY = 'homestream_admin_token';

function App() {
  const [activeTab, setActiveTab] = useState<TabType>('games');
  const [settingsSection, setSettingsSection] = useState<SettingsSection>('venue-bot-mapping');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [manualOperationOpen, setManualOperationOpen] = useState(true);
  const [analyticsOpen, setAnalyticsOpen] = useState(true);
  // Sidebar collapsed to an icon-only rail (default); remembered per browser.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('sidebarCollapsed') !== 'false';
    } catch {
      return true;
    }
  });
  const toggleSidebar = () => {
    setSidebarCollapsed((collapsed) => {
      try {
        localStorage.setItem('sidebarCollapsed', String(!collapsed));
      } catch {
        // storage unavailable; keep in-memory state only
      }
      return !collapsed;
    });
  };

  const isManualOperationTab = MANUAL_OPERATION_TABS.includes(activeTab);
  const manualOperationExpanded = manualOperationOpen || isManualOperationTab;

  const isAnalyticsTab = ANALYTICS_TABS.includes(activeTab);
  const analyticsExpanded = analyticsOpen || isAnalyticsTab;

  const selectManualOperationTab = (tab: TabType) => {
    setActiveTab(tab);
    setManualOperationOpen(true);
  };

  const selectAnalyticsTab = (tab: TabType) => {
    setActiveTab(tab);
    setAnalyticsOpen(true);
  };

  // TEMPORARY - glass style comparison: default "mesh", or ?glass=white for a pure white page.
  // Remove (with body[data-glass] in index.css) once a style is chosen.
  useEffect(() => {
    const glass = new URLSearchParams(window.location.search).get('glass');
    document.body.dataset.glass = glass === 'white' ? 'white' : 'mesh';
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    const authToken = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!authToken) {
      return;
    }

    authApi.me()
      .then((response) => {
        if (response.success) {
          setIsAuthenticated(true);
          setLoginError('');
          return;
        }

        localStorage.removeItem(AUTH_STORAGE_KEY);
      })
      .catch(() => {
        localStorage.removeItem(AUTH_STORAGE_KEY);
      });
  }, []);

  const handleLogin = async (email: string, password: string) => {
    try {
      const response = await authApi.login(email, password);

      if (response.success && response.data) {
        localStorage.setItem(AUTH_STORAGE_KEY, response.data.access_token);
        setIsAuthenticated(true);
        setLoginError('');
        return;
      }

      localStorage.removeItem(AUTH_STORAGE_KEY);
      setLoginError(response.error || 'Invalid email or password. Please try again.');
    } catch {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      setLoginError('Unable to reach the admin backend. Please try again.');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem(AUTH_STORAGE_KEY);
    setLoginError('');
  };

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} error={loginError} />;
  }

  return (
    <div className="app-container">
      <Toaster
        position="bottom-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: Colors.cardBackground,
            color: Colors.text,
            border: `1px solid ${withAlpha(Colors.border, 0.16)}`,
            fontSize: '14px',
          },
        }}
      />
      <header className="header">
        <div className="header-brand">
          <HomestreamLogo size="medium" variant="light" dropShadow />
          <div>
            <h1 className="header-title">Homestream Admin</h1>
            <p className="header-subtitle">Game Operations & Monitoring Dashboard</p>
          </div>
        </div>
        <div className="header-actions">
          <div className="header-status-pill">
            <div className="header-status-dot" />
            <span className="header-status-text">API Connected</span>
          </div>
          <button type="button" className="header-logout-btn" onClick={handleLogout}>
            <LogOut size={14} />
            Logout
          </button>
        </div>
      </header>

      <main className="main-content">
        <nav className={`tabs ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <button
            type="button"
            className="tabs-toggle"
            onClick={toggleSidebar}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!sidebarCollapsed}
          >
            {sidebarCollapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
          </button>
          <button
            className={`tab ${activeTab === 'games' ? 'active' : ''}`}
            title="Games"
            onClick={() => setActiveTab('games')}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <PiGameControllerDuotone size={18} />
              <span className="tab-label">Games</span>
            </span>
          </button>
          <button
            className={`tab ${activeTab === 'teams' ? 'active' : ''}`}
            title="School"
            onClick={() => setActiveTab('teams')}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <PiGraduationCapDuotone size={18} />
              <span className="tab-label">School</span>
            </span>
          </button>
          <button
            className={`tab ${activeTab === 'venues' ? 'active' : ''}`}
            title="Global Settings"
            onClick={() => setActiveTab('venues')}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <PiGearSixDuotone size={18} />
              <span className="tab-label">Global Settings</span>
            </span>
          </button>
          {THEME_MODE === 'dev' && (
            <button
              className={`tab ${activeTab === 'mock-genius' ? 'active' : ''}`}
              title="Mock Genius"
              onClick={() => setActiveTab('mock-genius')}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <PiSparkleDuotone size={18} />
                <span className="tab-label">Mock Genius</span>
              </span>
            </button>
          )}
          <button
            className={`tab tab-group ${isManualOperationTab ? 'active' : ''}`}
            title="Ops"
            style={{ display: 'flex', alignItems: 'center', justifyContent: sidebarCollapsed ? 'center' : 'space-between' }}
            onClick={() => setManualOperationOpen((open) => !open)}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <PiSquaresFourDuotone size={18} />
              <span className="tab-label">Ops</span>
            </span>
            {!sidebarCollapsed && (manualOperationExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
          </button>
          {manualOperationExpanded && (
            <div className="tab-subgroup">
              <button
                className={`tab tab-sub ${activeTab === 'iap-notification' ? 'active' : ''}`}
                title="In-App Notification"
                onClick={() => selectManualOperationTab('iap-notification')}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <PiBellDuotone size={18} />
                  <span className="tab-label">In-App Notification</span>
                </span>
              </button>
              <button
                className={`tab tab-sub ${activeTab === 'sync-detection' ? 'active' : ''}`}
                title="Sync Detection"
                onClick={() => selectManualOperationTab('sync-detection')}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <PiBroadcastDuotone size={18} />
                  <span className="tab-label">Sync Detection</span>
                </span>
              </button>
              <button
                className={`tab tab-sub ${activeTab === 'service-health' ? 'active' : ''}`}
                title="Service Health"
                onClick={() => selectManualOperationTab('service-health')}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <PiHeartbeatDuotone size={18} />
                  <span className="tab-label">Service Health</span>
                </span>
              </button>
              <button
                className={`tab tab-sub ${activeTab === 'manual-operation' ? 'active' : ''}`}
                title="Manual Operation"
                onClick={() => selectManualOperationTab('manual-operation')}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <PiWrenchDuotone size={18} />
                  <span className="tab-label">Manual Operation</span>
                </span>
              </button>
              <button
                className={`tab tab-sub ${activeTab === 'api-health' ? 'active' : ''}`}
                title="API Health"
                onClick={() => selectManualOperationTab('api-health')}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <PiCloudCheckDuotone size={18} />
                  <span className="tab-label">API Health</span>
                </span>
              </button>
            </div>
          )}
          <button
            className={`tab tab-group ${isAnalyticsTab ? 'active' : ''}`}
            title="Analytics"
            style={{ display: 'flex', alignItems: 'center', justifyContent: sidebarCollapsed ? 'center' : 'space-between' }}
            onClick={() => setAnalyticsOpen((open) => !open)}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <PiChartPieSliceDuotone size={18} />
              <span className="tab-label">Analytics</span>
            </span>
            {!sidebarCollapsed && (analyticsExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
          </button>
          {analyticsExpanded && (
            <div className="tab-subgroup">
              <button
                className={`tab tab-sub ${activeTab === 'analytics' ? 'active' : ''}`}
                title="User Audio Analytics"
                onClick={() => selectAnalyticsTab('analytics')}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <PiWaveformDuotone size={18} />
                  <span className="tab-label">User Audio Analytics</span>
                </span>
              </button>
              <button
                className={`tab tab-sub ${activeTab === 'purchasers' ? 'active' : ''}`}
                title="Purchasers"
                onClick={() => selectAnalyticsTab('purchasers')}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <PiReceiptDuotone size={18} />
                  <span className="tab-label">Purchasers</span>
                </span>
              </button>
              <button
                className={`tab tab-sub ${activeTab === 'purchase-timeline' ? 'active' : ''}`}
                title="Purchase Timeline"
                onClick={() => selectAnalyticsTab('purchase-timeline')}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <PiClockCounterClockwiseDuotone size={18} />
                  <span className="tab-label">Purchase Timeline</span>
                </span>
              </button>
              <button
                className={`tab tab-sub ${activeTab === 'paywall-counter' ? 'active' : ''}`}
                title="Paywall Counter"
                onClick={() => selectAnalyticsTab('paywall-counter')}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <PiGaugeDuotone size={18} />
                  <span className="tab-label">Paywall Counter</span>
                </span>
              </button>
              <button
                className={`tab tab-sub ${activeTab === 'contact-counter' ? 'active' : ''}`}
                title="Contact Counter"
                onClick={() => selectAnalyticsTab('contact-counter')}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <PiEnvelopeDuotone size={18} />
                  <span className="tab-label">Contact Counter</span>
                </span>
              </button>
            </div>
          )}
          {/*
          <button
            className={`tab ${activeTab === 'seasons' ? 'active' : ''}`}
            title="Seasons"
            onClick={() => setActiveTab('seasons')}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <PiCalendarDuotone size={18} />
              <span className="tab-label">Seasons</span>
            </span>
          </button>
          */}
          {/* <button
            className={`tab ${activeTab === 'executions' ? 'active' : ''}`}
            title="Executions"
            onClick={() => setActiveTab('executions')}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <PiPlayCircleDuotone size={18} />
              <span className="tab-label">Executions</span>
            </span>
          </button> */}
          {/* <button
            className={`tab ${activeTab === 'live-indexing' ? 'active' : ''}`}
            title="Manual Operation Legacy"
            onClick={() => setActiveTab('live-indexing')}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <PiVideoCameraDuotone size={18} />
              <span className="tab-label">Manual Operation Legacy</span>
            </span>
          </button> */}
          {/*
          <button
            className={`tab ${activeTab === 'image-matching' ? 'active' : ''}`}
            title="Image Matching"
            onClick={() => setActiveTab('image-matching')}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <PiImageDuotone size={18} />
              <span className="tab-label">Image Matching</span>
            </span>
          </button>
          */}
        </nav>

        <div className="tab-content">
          {/*
            All tabs are mounted at once (instead of only the active one) so that each panel's own
            on-mount data fetching fires immediately after login — manual or silent session-restore —
            rather than waiting for the user to click that tab. Inactive tabs are hidden via CSS
            (not unmounted), which keeps their state and avoids refetching when switching back.
          */}
          <div style={{ display: activeTab === 'games' ? 'block' : 'none' }}>
            <GamesPanel />
          </div>
          <div style={{ display: activeTab === 'teams' ? 'block' : 'none' }}>
            <TeamsPanel />
          </div>
          <div style={{ display: activeTab === 'venues' ? 'block' : 'none' }}>
            {/* Same layout as Manual Operation: section picker on the left, selected section on the right. */}
            <div className="section">
              <Card title="Global Settings" description="Venue bot mapping, affiliate offers, preview link expiry, internal accounts, and user team export">
                <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: '0 0 170px' }}>
                    {SETTINGS_SECTIONS.map((section) => {
                      const active = section.key === settingsSection;
                      return (
                        <button
                          key={section.key}
                          type="button"
                          onClick={() => setSettingsSection(section.key)}
                          className={`glass-option ${active ? 'glass-option--active' : ''}`}
                        >
                          <section.icon size={18} />
                          <span style={{ fontSize: '12.5px', fontWeight: 700, color: active ? Colors.secondary : Colors.text }}>{section.label}</span>
                        </button>
                      );
                    })}
                  </div>

                  <div style={{ alignSelf: 'stretch', width: '1px', background: 'rgba(var(--color-border-rgb), 0.16)' }} />

                  {/* Hidden, not unmounted, so unsaved edits survive switching sections. */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: settingsSection === 'venue-bot-mapping' ? 'block' : 'none' }}>
                      <VenuesPanel />
                    </div>
                    <div style={{ display: settingsSection === 'affiliate-offer' ? 'block' : 'none' }}>
                      <ProductsPanel />
                    </div>
                    <div style={{ display: settingsSection === 'preview-url-timer' ? 'block' : 'none' }}>
                      <PreviewUrlTimerSettings />
                    </div>
                    <div style={{ display: settingsSection === 'internal-accounts' ? 'block' : 'none' }}>
                      <InternalAccountsSettings />
                    </div>
                    <div style={{ display: settingsSection === 'user-team-export' ? 'block' : 'none' }}>
                      <UserTeamExportSettings />
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
          <div style={{ display: activeTab === 'analytics' ? 'block' : 'none' }}>
            <AnalyticsPanel />
          </div>
          {THEME_MODE === 'dev' && (
            <div style={{ display: activeTab === 'mock-genius' ? 'block' : 'none' }}>
              <MockGeniusPanel />
            </div>
          )}
          <div style={{ display: activeTab === 'iap-notification' ? 'block' : 'none' }}>
            <ManualOperationsPanel />
          </div>
          <div style={{ display: activeTab === 'sync-detection' ? 'block' : 'none' }}>
            <SyncDetectionPanel />
          </div>
          <div style={{ display: activeTab === 'service-health' ? 'block' : 'none' }}>
            <ServiceHealthPanel />
          </div>
          <div style={{ display: activeTab === 'infrastructure' ? 'block' : 'none' }}>
            <InfrastructurePanel />
          </div>
          <div style={{ display: activeTab === 'api-health' ? 'block' : 'none' }}>
            <ApiHealthPanel />
          </div>
          <div style={{ display: activeTab === 'manual-operation' ? 'block' : 'none' }}>
            <ManualOperationPanel />
          </div>
          <div style={{ display: activeTab === 'purchasers' ? 'block' : 'none' }}>
            <PurchasersPanel />
          </div>
          <div style={{ display: activeTab === 'purchase-timeline' ? 'block' : 'none' }}>
            <PurchaseTimelinePanel />
          </div>
          <div style={{ display: activeTab === 'paywall-counter' ? 'block' : 'none' }}>
            <PaywallCounterPanel />
          </div>
          <div style={{ display: activeTab === 'contact-counter' ? 'block' : 'none' }}>
            <ContactCounterPanel />
          </div>
          <div style={{ display: activeTab === 'seasons' ? 'block' : 'none' }}>
            <SeasonsPanel />
          </div>
          <div style={{ display: activeTab === 'executions' ? 'block' : 'none' }}>
            <ExecutionsPanel />
          </div>
          <div style={{ display: activeTab === 'live-indexing' ? 'block' : 'none' }}>
            <LiveUrlGameIndexingPanel />
          </div>
          {/*
          {activeTab === 'image-matching' && <ImageMatchingPanel />}
          */}
          {/*
          {activeTab === 'scheduler' && <SchedulerPanel />}
          */}
        </div>
      </main>

      <footer>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
          <HomestreamLogo size="small" variant="light" dropShadow />
          <span>Homestream Admin Dashboard v1.0.0</span>
        </div>
      </footer>
    </div>
  );
}

export default App;
